import { Badge, Card, Group, Loader, Stack, Text, Anchor } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';

import { BASE, unwrap } from '../api/client';
import { listMessages } from '../api/gen';
import type { Message } from '../api/gen';
import { useOrg } from '../api/org';
import { when } from '../lib/format';

/** The conversation itself, oldest first, the way a person reads one. */
export function ThreadView({ conversationId }: { conversationId: string }) {
  const org = useOrg();
  const { data, isLoading } = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: () => unwrap(listMessages({ path: { id: conversationId }, headers: org })),
  });

  if (isLoading) return <Loader size="sm" />;
  const messages = data?.messages ?? [];

  return (
    <Stack gap="sm">
      {messages.map((m) => (
        <Bubble key={m.id} message={m} />
      ))}
      {messages.length === 0 ? (
        <Text size="sm" c="dimmed">
          Nothing in this thread yet.
        </Text>
      ) : null}
    </Stack>
  );
}

function Bubble({ message }: { message: Message }) {
  const outbound = message.direction === 'outbound';
  return (
    <Card
      withBorder
      padding="sm"
      radius="md"
      ml={outbound ? 'xl' : 0}
      mr={outbound ? 0 : 'xl'}
      bg={outbound ? 'var(--mantine-color-blue-light)' : undefined}
    >
      <Group justify="space-between" mb={4}>
        <Group gap={6}>
          <Text size="xs" fw={600}>
            {message.author === 'contact' ? 'Customer' : message.author === 'agent' ? 'Agent' : 'Operator'}
          </Text>
          {message.delivery !== 'delivered' && message.delivery !== 'sent' ? (
            <Badge size="xs" color="red" variant="light">
              {message.delivery}
            </Badge>
          ) : null}
        </Group>
        <Text size="xs" c="dimmed">
          {when(message.createdAt)}
        </Text>
      </Group>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {message.body}
      </Text>
      {message.attachments.length > 0 ? (
        <Group gap="xs" mt="xs">
          {message.attachments.map((a, i) => (
            // A download, never a preview: these are bytes a stranger chose,
            // and the server serves them with Content-Disposition to match.
            <Anchor
              key={a.key}
              size="xs"
              href={`${BASE}/messages/${message.id}/attachments/${i}`}
              target="_blank"
              rel="noreferrer"
            >
              {a.filename || 'attachment'}
            </Anchor>
          ))}
        </Group>
      ) : null}
    </Card>
  );
}
